import { ActionItemBreadcrumb, SubtaskItem } from "./types";
import { clampDateToParentDueDate, formatDateOnly } from "./utils";

export const resolveIsSubTaskFlag = (item?: any): boolean =>
  Boolean(item?.isSubTask === true || item?.parentActionItemId);

const withConvertFlags = (row: any, isAccepted: boolean) => ({
  ...row,
  isTask: isAccepted,
  isApproved: isAccepted,
  isDiscarded: !isAccepted,
});

export const patchConvertedActionItemRows = (
  list: any[] = [],
  actionItemIds: string[] = [],
  isAccepted: boolean
): any[] =>
  list.map((row) => {
    const converted = actionItemIds.includes(row.id);
    const children = row.subRows || row.subTasks;
    const nested =
      Array.isArray(children) && children.length
        ? patchConvertedActionItemRows(children, actionItemIds, isAccepted).map((child) =>
            converted ? withConvertFlags(child, isAccepted) : child
          )
        : undefined;
    const nextRow = converted ? withConvertFlags(row, isAccepted) : row;
    if (!nested) return nextRow;
    return { ...nextRow, subRows: nested, subTasks: nested };
  });

export const collectClampedSubTaskDueDates = (
  parent: any,
  parentDueDate: string | null | undefined
): { actionItemId: string; dueDate: string }[] => {
  if (!parent || !parentDueDate) return [];
  const children = parent.subRows || parent.subTasks || [];
  if (!Array.isArray(children)) return [];

  return children
    .map((child: any) => {
      const data = child?.original ?? child;
      const actionItemId = data?.id || data?.actionItemId;
      if (!actionItemId) return null;
      const currentDue = data?.dueDate;
      const clamped = currentDue
        ? clampDateToParentDueDate(currentDue, parentDueDate)
        : undefined;
      const dueDate =
        (typeof clamped === "string" && clamped) ||
        (currentDue ? formatDateOnly(currentDue) : undefined);
      if (!dueDate) return null;
      return { actionItemId, dueDate };
    })
    .filter(Boolean) as { actionItemId: string; dueDate: string }[];
};

export const normalizeSubtasks = (details: any): SubtaskItem[] => {
  const raw = details?.subTasks ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => ({
      actionItemId: item?.actionItemId ?? "",
      title: item?.actionItemTitle ?? "",
      assigneeName: item?.assigneeName ?? null,
      assigneeEmail: item?.assigneeEmail ?? null,
      assigneePictureUrl: item?.assigneePictureUrl ?? null,
      dueDate: item?.dueDate ?? null,
      status: item?.stage ?? null,
      rowKey: item?.actionItemId,
      isAiGenerated: item?.isAiGenerated ?? false,
    }))
    .filter((item: SubtaskItem & { rowKey: string }) => !!item.actionItemId || !!item.title);
};

export const getParentActionItem = (
  details: any,
  selectedActionItem?: any
): ActionItemBreadcrumb | null => {
  const parent =
    details?.parentActionItem ??
    (details?.parent && (details.parent.actionItemId || details.parent.id)
      ? details.parent
      : null);
  const parentId =
    parent?.actionItemId ??
    details?.parentActionItemId ??
    selectedActionItem?.parentActionItemId ??
    null;
  if (!parentId) return null;
  const title =
    parent?.actionItemTitle ??
    details?.parentActionItemTitle ??
    selectedActionItem?.parentActionItemTitle ??
    "Parent task";
  return {
    id: parentId,
    title,
    dueDate:
      parent?.dueDate ??
      details?.parentDueDate ??
      selectedActionItem?.parentDueDate ??
      selectedActionItem?.parentActionItem?.dueDate ??
      null,
  };
};

export const getAncestorBreadcrumbs = (
  details: any,
  selectedActionItem?: any
): ActionItemBreadcrumb[] => {
  const ancestors = details?.ancestors ?? details?.parentPath ?? [];
  if (Array.isArray(ancestors) && ancestors.length > 0) {
    return ancestors
      .map((item: any) => ({
        id: item?.actionItemId ?? "",
        title: item?.actionItemTitle ?? "",
        dueDate: item?.dueDate ?? null,
      }))
      .filter((item: ActionItemBreadcrumb) => !!item.id);
  }
  const parent = getParentActionItem(details, selectedActionItem);
  return parent ? [parent] : [];
};
